/**
 * ClinicalInsights.jsx
 * 
 * Primary landing page and command center for healthcare professionals.
 * Displays pending reviews, active concerns, and summary metrics.
 * Designed to minimize cognitive load by surfacing critical information first.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  RefreshCw, 
  AlertCircle, 
  MessageSquare, 
  Clock, 
  Check, 
  ChevronDown,
  ChevronRight,
  Eye,
  Filter,
  User,
  Calendar
} from 'lucide-react'
import { mdApi } from '../api/mdApi'
import { getSeverityColor } from '../config/theme'
import './ClinicalInsights.css'

// ============================================================================
// Constants
// ============================================================================

/**
 * Severity levels with their display properties
 * Following the clinical color coding from the implementation plan
 */
const SEVERITY_CONFIG = {
  urgent: { label: 'Urgent', color: '#DC3545', icon: '🔴' },
  high: { label: 'High', color: '#FD7E14', icon: '🟠' },
  medium: { label: 'Medium', color: '#FFC107', icon: '🟡' },
  low: { label: 'Low', color: '#28A745', icon: '🟢' },
}

/**
 * Resolution options for quick actions
 */
const RESOLUTION_OPTIONS = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'already_known', label: 'Already Known' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'not_applicable', label: 'Not Applicable' },
]

/**
 * Time filter options
 */
const TIME_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'all', label: 'All Time' },
]

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format relative time with overdue detection
 * @param {string} dateString - ISO date string
 * @returns {Object} { text: string, isOverdue: boolean }
 */
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  // Determine if overdue (> 24 hours)
  const isOverdue = diffHours >= 24

  if (diffMins < 1) {
    return { text: 'Just now', isOverdue: false }
  } else if (diffMins < 60) {
    return { text: `${diffMins} min ago`, isOverdue: false }
  } else if (diffHours < 24) {
    return { text: `${diffHours}h ago`, isOverdue: false }
  } else {
    return { text: `${diffDays} days ago - OVERDUE`, isOverdue: true }
  }
}

/**
 * Filter items by time range
 * @param {Array} items - Items with created_at or date field
 * @param {string} filter - 'today', 'week', or 'all'
 * @returns {Array} Filtered items
 */
const filterByTime = (items, filter) => {
  if (filter === 'all') return items
  
  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfWeek = new Date(startOfDay)
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
  
  return items.filter(item => {
    const itemDate = new Date(item.created_at || item.date)
    if (filter === 'today') {
      return itemDate >= startOfDay
    } else if (filter === 'week') {
      return itemDate >= startOfWeek
    }
    return true
  })
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * SummaryCard - Displays a metric count with severity color
 */
const SummaryCard = ({ title, count, severity, onClick, isActive }) => {
  const config = SEVERITY_CONFIG[severity] || { color: '#6B7280' }
  
  return (
    <button 
      className={`summary-card ${isActive ? 'active' : ''}`}
      style={{ '--card-accent-color': config.color }}
      onClick={onClick}
      aria-label={`${count} ${title}`}
    >
      <div className="summary-card-count">{count}</div>
      <div className="summary-card-title">{title}</div>
    </button>
  )
}

/**
 * ConversationItem - Individual conversation requiring review
 */
const ConversationItem = ({ conversation, onMarkReviewed, onView, onPatientClick }) => {
  const timeInfo = formatRelativeTime(conversation.created_at || conversation.date)
  
  /**
   * Handle patient name click - navigate to patient detail page
   */
  const handlePatientNameClick = (e) => {
    e.stopPropagation()
    if (onPatientClick && conversation.patient_id) {
      onPatientClick(conversation.patient_id)
    }
  }
  
  return (
    <div className="conversation-item">
      <div className="conversation-content">
        <div className="conversation-header">
          <span
            className="patient-name patient-name-link"
            onClick={handlePatientNameClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handlePatientNameClick(e)}
          >
            <User size={14} />
            {conversation.patient_name}
          </span>
          <span className={`conversation-time ${timeInfo.isOverdue ? 'overdue' : ''}`}>
            <Clock size={12} />
            {timeInfo.text}
          </span>
        </div>
        <div className="conversation-topic">
          Topic: {conversation.summary || conversation.topics_discussed?.join(', ') || 'General conversation'}
        </div>
        {conversation.has_concerning_issues && (
          <span className="concerning-badge">
            <AlertCircle size={12} />
            Contains concerns
          </span>
        )}
      </div>
      <div className="conversation-actions">
        <button 
          className="btn-action btn-view"
          onClick={() => onView(conversation)}
          aria-label="View conversation"
        >
          <Eye size={14} />
          View
        </button>
        <button 
          className="btn-action btn-mark-reviewed"
          onClick={() => onMarkReviewed(conversation.id)}
          aria-label="Mark as reviewed"
        >
          <Check size={14} />
          Mark Reviewed
        </button>
      </div>
    </div>
  )
}

/**
 * ConcernItem - Individual concern with inline resolution
 */
const ConcernItem = ({ concern, onResolve, onViewDetails, isExpanded, onToggleExpand }) => {
  const [selectedResolution, setSelectedResolution] = useState('')
  const [notes, setNotes] = useState('')
  const [showNotes, setShowNotes] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  
  const config = SEVERITY_CONFIG[concern.severity] || SEVERITY_CONFIG.medium
  const timeInfo = formatRelativeTime(concern.created_at)
  
  const handleResolve = async (resolution) => {
    if (!resolution) return
    
    setIsResolving(true)
    try {
      // Use concern_id (the string ID like "CONCERN-xxx") for the API call
      const idForApi = concern.concern_id || concern.id
      await onResolve(idForApi, {
        status: resolution,
        notes: notes,
        resolved_by: 'Current User', // In production, get from auth context
      })
    } finally {
      setIsResolving(false)
    }
  }
  
  const handleQuickAcknowledge = () => {
    handleResolve('acknowledged')
  }
  
  return (
    <div 
      className={`concern-item severity-${concern.severity}`}
      style={{ '--severity-color': config.color }}
    >
      <div className="concern-header" onClick={() => onToggleExpand(concern.id)}>
        <div className="concern-severity">
          <span className="severity-icon">{config.icon}</span>
          <span className="severity-label">{config.label.toUpperCase()}</span>
        </div>
        <div className="concern-title">
          {concern.description || concern.concern_type}
        </div>
        <span className="expand-icon">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </div>
      
      <div className="concern-meta">
        <span className="patient-info">
          <User size={12} />
          {concern.patient_name}
        </span>
        <span className={`concern-time ${timeInfo.isOverdue ? 'overdue' : ''}`}>
          <Clock size={12} />
          {timeInfo.text}
        </span>
      </div>
      
      {isExpanded && (
        <div className="concern-details">
          {concern.ai_recommendation && (
            <div className="ai-recommendation">
              <strong>AI Recommendation:</strong> {concern.ai_recommendation}
            </div>
          )}
          
          {concern.is_known_condition !== undefined && (
            <div className="known-status">
              <span className={concern.is_known_condition ? 'known' : 'new'}>
                {concern.is_known_condition ? '📋 Known condition' : '🆕 New finding'}
              </span>
            </div>
          )}
          
          <div className="concern-source">
            <Calendar size={12} />
            From conversation on {new Date(concern.created_at).toLocaleDateString()}
          </div>
        </div>
      )}
      
      <div className="concern-actions">
        <button 
          className="btn-action btn-acknowledge"
          onClick={handleQuickAcknowledge}
          disabled={isResolving}
          aria-label="Acknowledge concern"
        >
          <Check size={14} />
          {isResolving ? 'Processing...' : 'Acknowledge'}
        </button>
        
        <div className="resolution-dropdown">
          <select
            value={selectedResolution}
            onChange={(e) => {
              setSelectedResolution(e.target.value)
              if (e.target.value && !showNotes) {
                handleResolve(e.target.value)
              }
            }}
            disabled={isResolving}
            aria-label="Select resolution"
          >
            <option value="">Resolution...</option>
            {RESOLUTION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        
        <button 
          className="btn-action btn-notes-toggle"
          onClick={() => setShowNotes(!showNotes)}
          aria-label={showNotes ? 'Hide notes' : 'Add notes'}
        >
          {showNotes ? 'Hide Notes' : 'Add Notes'}
        </button>
        
        <button 
          className="btn-action btn-view-details"
          onClick={() => onViewDetails(concern)}
          aria-label="View concern details"
        >
          <Eye size={14} />
          View Details
        </button>
      </div>
      
      {showNotes && (
        <div className="concern-notes">
          <textarea
            placeholder="Add resolution notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            aria-label="Resolution notes"
          />
          <button 
            className="btn-save-notes"
            onClick={() => selectedResolution && handleResolve(selectedResolution)}
            disabled={!selectedResolution || isResolving}
          >
            Save with Notes
          </button>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

function ClinicalInsights() {
  const navigate = useNavigate()
  
  // State for data
  const [pendingReviews, setPendingReviews] = useState([])
  const [concerns, setConcerns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // State for UI
  const [lastUpdated, setLastUpdated] = useState(null)
  const [timeFilter, setTimeFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState(null)
  const [expandedConcerns, setExpandedConcerns] = useState(new Set())
  const [selectedItems, setSelectedItems] = useState(new Set())
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  /**
   * Load all data for the Clinical Insights page
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [reviewsData, concernsData] = await Promise.all([
        mdApi.getPendingReviews(),
        mdApi.getPendingConcerns(severityFilter),
      ])
      
      setPendingReviews(reviewsData || [])
      setConcerns(concernsData || [])
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error loading clinical insights:', err)
      setError('Failed to load clinical insights. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [severityFilter])
  
  // Initial data load
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // ============================================================================
  // Computed Values
  // ============================================================================
  
  /**
   * Filter and count concerns by severity
   */
  const concernCounts = useMemo(() => {
    const filteredConcerns = filterByTime(concerns, timeFilter)
    return {
      urgent: filteredConcerns.filter(c => c.severity === 'urgent').length,
      high: filteredConcerns.filter(c => c.severity === 'high').length,
      medium: filteredConcerns.filter(c => c.severity === 'medium').length,
      low: filteredConcerns.filter(c => c.severity === 'low').length,
      total: filteredConcerns.length,
    }
  }, [concerns, timeFilter])
  
  /**
   * Filtered pending reviews
   */
  const filteredReviews = useMemo(() => {
    return filterByTime(pendingReviews, timeFilter)
  }, [pendingReviews, timeFilter])
  
  /**
   * Filtered concerns based on severity filter
   */
  const filteredConcerns = useMemo(() => {
    let filtered = filterByTime(concerns, timeFilter)
    if (severityFilter) {
      filtered = filtered.filter(c => c.severity === severityFilter)
    }
    // Sort by severity (urgent first) then by date
    return filtered.sort((a, b) => {
      const severityOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
      const orderDiff = (severityOrder[a.severity] || 4) - (severityOrder[b.severity] || 4)
      if (orderDiff !== 0) return orderDiff
      return new Date(b.created_at) - new Date(a.created_at)
    })
  }, [concerns, timeFilter, severityFilter])
  
  // ============================================================================
  // Event Handlers
  // ============================================================================
  
  /**
   * Handle manual refresh
   */
  const handleRefresh = () => {
    loadData()
  }
  
  /**
   * Navigate to patient detail for a conversation
   */
  const handleViewConversation = (conversation) => {
    navigate(`/patients/${conversation.patient_id}`)
  }
  
  /**
   * Mark a conversation as reviewed
   */
  const handleMarkReviewed = async (conversationId) => {
    try {
      // API call would go here - for now, remove from local state
      setPendingReviews(prev => prev.filter(r => r.id !== conversationId))
    } catch (err) {
      console.error('Error marking conversation as reviewed:', err)
    }
  }
  
  /**
   * Resolve a concern
   * Note: concernId is the concern_id string (e.g., "CONCERN-xxx")
   */
  const handleResolveConcern = async (concernId, resolution) => {
    try {
      await mdApi.resolveConcern(concernId, resolution)
      // Filter by concern_id since that's what we passed to the API
      setConcerns(prev => prev.filter(c => (c.concern_id || c.id) !== concernId))
    } catch (err) {
      console.error('Error resolving concern:', err)
      throw err // Re-throw to let the component handle the error
    }
  }
  
  /**
   * View concern details (navigate to patient)
   */
  const handleViewConcernDetails = (concern) => {
    navigate(`/patients/${concern.patient_id}`)
  }
  
  /**
   * Toggle concern expansion
   */
  const handleToggleExpand = (concernId) => {
    setExpandedConcerns(prev => {
      const newSet = new Set(prev)
      if (newSet.has(concernId)) {
        newSet.delete(concernId)
      } else {
        newSet.add(concernId)
      }
      return newSet
    })
  }
  
  /**
   * Handle summary card click to filter by severity
   */
  const handleSeverityCardClick = (severity) => {
    setSeverityFilter(prev => prev === severity ? null : severity)
  }
  
  /**
   * Handle batch acknowledge
   * Note: selectedItems contains concern_id strings
   */
  const handleBatchAcknowledge = async () => {
    if (selectedItems.size === 0) return
    
    // In production, this would be a batch API call
    for (const concernId of selectedItems) {
      try {
        await mdApi.resolveConcern(concernId, {
          status: 'acknowledged',
          resolved_by: 'Current User',
          notes: 'Batch acknowledged',
        })
      } catch (err) {
        console.error(`Error acknowledging concern ${concernId}:`, err)
      }
    }
    
    // Filter by concern_id since that's what we store in selectedItems
    setConcerns(prev => prev.filter(c => !selectedItems.has(c.concern_id || c.id)))
    setSelectedItems(new Set())
  }
  
  /**
   * Toggle item selection
   * Note: We store concern_id (string) in selectedItems for API compatibility
   */
  const handleToggleSelect = (concernId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(concernId)) {
        newSet.delete(concernId)
      } else {
        newSet.add(concernId)
      }
      return newSet
    })
  }
  
  // ============================================================================
  // Render
  // ============================================================================
  
  if (loading && !lastUpdated) {
    return (
      <div className="clinical-insights loading-state">
        <div className="spinner"></div>
        <p>Loading clinical insights...</p>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="clinical-insights error-state">
        <AlertCircle size={48} />
        <h2>Unable to Load Data</h2>
        <p>{error}</p>
        <button onClick={handleRefresh} className="btn-retry">
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    )
  }
  
  return (
    <div className="clinical-insights">
      {/* Page Header */}
      <header className="insights-header">
        <div className="header-title-section">
          <h1>Clinical Insights</h1>
          <p className="header-subtitle">
            Command center for patient care priorities
          </p>
        </div>
        
        <div className="header-controls">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={handleRefresh} 
            className="btn-refresh"
            disabled={loading}
            aria-label="Refresh data"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </header>
      
      {/* Summary Cards Row */}
      <section className="summary-cards" aria-label="Summary metrics">
        <SummaryCard
          title="Pending Reviews"
          count={filteredReviews.length}
          severity="medium"
          onClick={() => {}}
          isActive={false}
        />
        <SummaryCard
          title="Urgent Concerns"
          count={concernCounts.urgent}
          severity="urgent"
          onClick={() => handleSeverityCardClick('urgent')}
          isActive={severityFilter === 'urgent'}
        />
        <SummaryCard
          title="High Concerns"
          count={concernCounts.high}
          severity="high"
          onClick={() => handleSeverityCardClick('high')}
          isActive={severityFilter === 'high'}
        />
        <SummaryCard
          title="Medium Concerns"
          count={concernCounts.medium}
          severity="medium"
          onClick={() => handleSeverityCardClick('medium')}
          isActive={severityFilter === 'medium'}
        />
      </section>
      
      {/* Time Filter */}
      <div className="filter-bar">
        <div className="filter-group">
          <Filter size={14} />
          <span>Filter:</span>
          {TIME_FILTERS.map(filter => (
            <button
              key={filter.value}
              className={`filter-btn ${timeFilter === filter.value ? 'active' : ''}`}
              onClick={() => setTimeFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        
        {selectedItems.size > 0 && (
          <div className="batch-actions">
            <span>{selectedItems.size} selected</span>
            <button 
              className="btn-batch-acknowledge"
              onClick={handleBatchAcknowledge}
            >
              Acknowledge All Selected
            </button>
          </div>
        )}
      </div>
      
      {/* Main Content - Two Column Layout */}
      <div className="insights-grid">
        {/* Left Column - Conversations Needing Review */}
        <section className="insights-panel conversations-panel">
          <div className="panel-header">
            <h2>
              <MessageSquare size={20} />
              Conversations Needing Review
            </h2>
            <span className="panel-count">{filteredReviews.length}</span>
          </div>
          
          <div className="panel-content">
            {filteredReviews.length === 0 ? (
              <div className="empty-state">
                <Check size={32} />
                <p>All conversations reviewed!</p>
              </div>
            ) : (
              <div className="items-list">
                {filteredReviews.map(conversation => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    onView={handleViewConversation}
                    onMarkReviewed={handleMarkReviewed}
                    onPatientClick={(patientId) => navigate(`/patients/${patientId}`)}
                  />
                ))}
              </div>
            )}
            
            {filteredReviews.length > 0 && (
              <button className="btn-load-more">
                Load More...
              </button>
            )}
          </div>
        </section>
        
        {/* Right Column - Active Concerns */}
        <section className="insights-panel concerns-panel">
          <div className="panel-header">
            <h2>
              <AlertCircle size={20} />
              Active Concerns
              {severityFilter && (
                <span className="filter-indicator">
                  (Filtered: {SEVERITY_CONFIG[severityFilter]?.label})
                </span>
              )}
            </h2>
            <span className="panel-count">{filteredConcerns.length}</span>
          </div>
          
          <div className="panel-content">
            {filteredConcerns.length === 0 ? (
              <div className="empty-state">
                <Check size={32} />
                <p>No active concerns!</p>
              </div>
            ) : (
              <div className="items-list">
                {filteredConcerns.map(concern => (
                  <div key={concern.concern_id || concern.id} className="concern-wrapper">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(concern.concern_id || concern.id)}
                      onChange={() => handleToggleSelect(concern.concern_id || concern.id)}
                      className="concern-checkbox"
                      aria-label={`Select concern: ${concern.description}`}
                    />
                    <ConcernItem
                      concern={concern}
                      onResolve={handleResolveConcern}
                      onViewDetails={handleViewConcernDetails}
                      isExpanded={expandedConcerns.has(concern.id)}
                      onToggleExpand={handleToggleExpand}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {filteredConcerns.length > 0 && (
              <button className="btn-load-more">
                Load More...
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default ClinicalInsights